import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, MessageCircle, Truck, Sparkles, Check, Smartphone, Tag, Watch, Aperture, Zap, Cable, Package } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { absUrl, buildWhatsAppUrl } from "@/lib/helpers";
import ProductCard from "@/components/ProductCard";

const FEATURE_ICONS = [Sparkles, MessageCircle, ShieldCheck, Truck];
const CAT_ICON = {
  Casing: Smartphone,
  Antigores: ShieldCheck,
  "Strap Apple Watch": Watch,
  "Ring Camera": Aperture,
  Charger: Zap,
  Kabel: Cable,
};
const SIZE = { sm: "text-xs sm:text-sm", base: "text-sm sm:text-base", lg: "text-base sm:text-lg", xl: "text-lg sm:text-xl" };
const ALL = "all";

export default function Home() {
  const { settings } = useStore();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [catModels, setCatModels] = useState([]);
  const [category, setCategory] = useState(null);
  const [model, setModel] = useState(ALL);

  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const [promo, setPromo] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true, page_size: 8 } }).then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/products", { params: { promo: true, page_size: 8 } }).then((r) => setPromo(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!category) { setCatModels([]); return; }
    api.get("/phone-models", { params: { category_id: category } }).then((r) => setCatModels(r.data)).catch(() => setCatModels([]));
  }, [category]);

  useEffect(() => {
    if (!category) { setResults(null); return; }
    setLoadingResults(true);
    const p = { category_id: category, page_size: 200 };
    if (model !== ALL) p.model_id = model;
    api.get("/products", { params: p })
      .then((r) => setResults(r.data.items))
      .catch(() => setResults([]))
      .finally(() => setLoadingResults(false));
  }, [model, category]);

  useEffect(() => {
    if (model !== ALL) api.post("/track/search", { query: "", model_id: model }).catch(() => {});
  }, [model]);

  const selectCategory = (id) => {
    setCategory((prev) => (prev === id ? null : id));
    setModel(ALL);
    setResults(null);
  };

  const selectedCategory = categories.find((c) => c.id === category);
  const selectedModel = catModels.find((m) => m.id === model);
  const showTypePanel = category && selectedCategory?.require_type !== false;

  const askAdmin = () => {
    const text = `Halo Admin ${settings?.store_name}, saya mencari ${selectedCategory?.name || "produk"} untuk ${selectedModel?.name || "tipe HP tertentu"} tapi belum tersedia. Bisa dibantu?`;
    window.open(buildWhatsAppUrl(settings?.whatsapp_number, text), "_blank");
  };

  const chatAdmin = () => {
    const text = `Halo Admin ${settings?.store_name || "Upplesories"}, saya ingin konsultasi tentang produk yang saya cari.`;
    api.post("/track/whatsapp-click", { product_id: "hero-chat-admin" }).catch(() => {});
    window.open(buildWhatsAppUrl(settings?.whatsapp_number, text), "_blank");
  };

  const gotoCatalog = () => {
    const sp = new URLSearchParams();
    if (category) sp.set("category", category);
    if (model !== ALL) sp.set("model", model);
    navigate(`/katalog?${sp.toString()}`);
  };

  const resultsTitle = model !== ALL ? `${selectedCategory?.name} untuk ${selectedModel?.name}` : `Semua ${selectedCategory?.name}`;

  const TypePanel = () => (
    <div data-testid="finder-type-panel" style={{ backgroundColor: settings?.finder_panel_color || "#FAF9F6" }} className="mx-auto max-w-xl animate-scale-in rounded-2xl border border-black/10 p-4 shadow-lg shadow-black/25">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Smartphone className="h-4 w-4 text-accent" /> Pilih Tipe HP untuk <span className="text-accent">{selectedCategory?.name}</span>
      </label>
      <Select value={model} onValueChange={setModel}>
        <SelectTrigger data-testid="finder-model-select" className="mt-2.5 border-slate-300 bg-white text-slate-800 focus:ring-accent">
          <SelectValue placeholder="Pilih tipe / model HP" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ALL}>Semua tipe HP</SelectItem>
          {catModels.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {catModels.length === 0 && <p className="mt-2 text-xs text-slate-500">Produk kategori ini cocok untuk semua tipe HP.</p>}
    </div>
  );

  return (
    <div>
      {/* FUTURISTIC FINDER HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        {settings?.hero_image_url && <img src={absUrl(settings.hero_image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.06]" />}
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
              <Sparkles className="h-3.5 w-3.5" /> {settings?.store_name || "UPPLESORIES"}
            </span>
            <h1 className="mx-auto mt-4 max-w-3xl font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-balance">
              {settings?.finder_title || "Selamat datang! Cari Casing untuk Tipe HP apa?"}
            </h1>
            <p className="mx-auto mt-3 max-w-lg text-sm sm:text-base text-primary-foreground/70">
              {settings?.finder_subtitle || "Pilih kategori dan tipe HP kamu, kami tampilkan produk yang kompatibel."}
            </p>
            {settings?.whatsapp_number && (
              <button
                data-testid="hero-chat-admin-btn"
                onClick={chatAdmin}
                className="group mx-auto mt-5 inline-flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:scale-105 active:scale-95"
              >
                <svg viewBox="0 0 32 32" className="h-5 w-5 fill-current">
                  <path d="M19.11 17.2c-.28-.14-1.63-.8-1.88-.89-.25-.09-.44-.14-.62.14-.18.28-.71.89-.87 1.07-.16.18-.32.2-.6.07-.28-.14-1.17-.43-2.23-1.38-.82-.73-1.38-1.64-1.54-1.92-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.48.14-.16.18-.28.28-.46.09-.18.05-.35-.02-.49-.07-.14-.62-1.5-.85-2.05-.22-.53-.45-.46-.62-.47l-.53-.01c-.18 0-.48.07-.73.35-.25.28-.96.94-.96 2.29s.98 2.66 1.12 2.84c.14.18 1.93 2.95 4.68 4.14.65.28 1.16.45 1.56.58.66.21 1.25.18 1.72.11.53-.08 1.63-.67 1.86-1.31.23-.64.23-1.19.16-1.31-.07-.12-.25-.19-.53-.33M16.05 26.5h-.01c-1.74 0-3.44-.47-4.93-1.35l-.35-.21-3.67.96.98-3.58-.23-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.83 9.83 0 0 1 2.9 7c0 5.45-4.44 9.89-9.89 9.89m8.42-18.3A11.8 11.8 0 0 0 16.05 4.7C9.52 4.7 4.2 10.02 4.2 16.55c0 2.09.55 4.13 1.58 5.93L4.1 28.4l6.06-1.59a11.83 11.83 0 0 0 5.66 1.44h.01c6.53 0 11.85-5.32 11.85-11.85 0-3.17-1.23-6.14-3.47-8.38" />
                </svg>
                Chat Admin
              </button>
            )}
          </div>

          {/* Type sub-selection appears ABOVE the boxes */}
          {showTypePanel && <div className="mt-8"><TypePanel /></div>}

          {/* Category boxes (smaller, customizable per category) */}
          <div className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
            {categories.map((c, i) => {
              const Icon = CAT_ICON[c.name] || Package;
              const active = category === c.id;
              const boxStyle = c.color
                ? { backgroundColor: active ? `${c.color}2b` : `${c.color}14`, borderColor: active ? c.color : `${c.color}55`, boxShadow: active ? `0 0 28px -8px ${c.color}` : "none" }
                : {};
              const iconStyle = c.color ? { backgroundColor: active ? c.color : `${c.color}26`, color: active ? "#fff" : c.color } : {};
              const labelStyle = c.text_color ? { color: c.text_color } : {};
              return (
                <button
                  key={c.id}
                  data-testid={`finder-cat-${c.id}`}
                  onClick={() => selectCategory(c.id)}
                  style={{ animationDelay: `${i * 55}ms`, ...boxStyle }}
                  className={`group relative overflow-hidden rounded-xl border p-4 text-left animate-fade-up transition-all duration-300 ${
                    c.color ? "" : active ? "border-accent bg-accent/15 shadow-[0_0_28px_-8px] shadow-accent/60" : "border-white/10 bg-white/5 hover:border-accent/50 hover:bg-white/10"
                  } hover:-translate-y-0.5`}
                >
                  {c.image_url ? (
                    <img src={absUrl(c.image_url)} alt={c.name} className={`h-9 w-9 rounded-lg object-cover ring-1 ${active ? "ring-accent" : "ring-white/20"}`} />
                  ) : (
                    <div style={iconStyle} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${c.color ? "" : active ? "bg-accent text-accent-foreground" : "bg-white/10 text-accent group-hover:bg-accent/20"}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <p style={labelStyle} className={`mt-2.5 font-heading font-semibold leading-tight ${SIZE[c.text_size] || SIZE.base}`}>{c.name}</p>
                  <p style={labelStyle} className="text-[11px] opacity-60">{c.require_type === false ? "Lihat produk" : "Pilih tipe HP"}</p>
                  <ArrowRight className={`absolute right-3.5 top-4 h-4 w-4 transition-all duration-300 ${active ? "translate-x-0 text-accent opacity-100" : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-60"}`} />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINDER RESULTS */}
      {category && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 animate-fade-in" data-testid="finder-results">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <h2 className="flex items-center gap-2 font-heading text-xl sm:text-2xl font-bold tracking-tight">
              <Smartphone className="h-5 w-5 text-accent" /> {resultsTitle}
            </h2>
            {results && results.length > 0 && (
              <button onClick={gotoCatalog} className="flex items-center gap-1 text-sm font-semibold text-accent hover:underline">Lihat semua <ArrowRight className="h-4 w-4" /></button>
            )}
          </div>

          {loadingResults || results === null ? (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}
            </div>
          ) : results.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-dashed border-border bg-card p-8 text-center">
              <p className="font-heading font-semibold">Produk untuk tipe HP ini belum tersedia.</p>
              <p className="mt-1 text-sm text-muted-foreground">Hubungi admin untuk menanyakan ketersediaannya.</p>
              <button data-testid="finder-ask-admin" onClick={askAdmin} className="mt-4 inline-flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm font-semibold text-white hover:opacity-90">
                <MessageCircle className="h-4 w-4" /> Tanya Admin via WhatsApp
              </button>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
              {results.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </section>
      )}

      {/* PROMO */}
      {promo.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
          <div className="flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              <Tag className="h-6 w-6 text-rose-500" /> Promo Spesial
            </h2>
            <Link to="/katalog" className="flex items-center gap-1 text-sm font-semibold text-accent hover:underline">Lihat semua <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6" data-testid="promo-grid">
            {promo.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Produk Unggulan</h2>
            <Link to="/katalog" className="flex items-center gap-1 text-sm font-semibold text-accent hover:underline">Lihat semua <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* FEATURES */}
      {(settings?.features || []).length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
          {settings?.features_title && <h2 className="mb-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight">{settings.features_title}</h2>}
          <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
            {(settings?.features || []).map((f, i) => {
              const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <div key={i} className="rounded-xl border border-border bg-card p-3.5 sm:p-5 transition-all hover:shadow-md">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent sm:h-11 sm:w-11"><Icon className="h-4 w-4 sm:h-5 sm:w-5" /></div>
                  <h3 className="mt-2.5 font-heading text-sm sm:text-base font-semibold leading-tight">{f.title}</h3>
                  <p className="mt-1 text-xs sm:text-sm text-muted-foreground">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* HOW TO ORDER */}
      {(settings?.how_to_order || []).length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
          <div className="rounded-2xl sm:rounded-3xl bg-primary p-5 sm:p-10 text-primary-foreground">
            <h2 className="font-heading text-xl sm:text-3xl font-bold tracking-tight">{settings?.how_to_order_title || "Cara Memesan"}</h2>
            <div className="mt-4 grid grid-cols-2 gap-2.5 sm:mt-6 sm:gap-4 lg:grid-cols-4">
              {(settings?.how_to_order || []).map((step, i) => (
                <div key={i} className="rounded-xl bg-primary-foreground/5 p-3.5 sm:p-5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold text-xs sm:text-sm">{i + 1}</div>
                  <p className="mt-2 flex gap-1.5 text-xs sm:text-sm text-primary-foreground/85"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
